// ATTICUS-ANALYTICS-COMMAND-CENTER-1 — Commercial lens.
//
// Book-of-business view: claims by insured / vessel / business line, new-claims
// trend for top clients, high-exposure clients, and (partial) underwriter
// market exposure.
import React, { useEffect, useState, useMemo } from "react";
import { apiFetch, fmt } from "../../api";
import { KpiCard, ChartCard, buildQS } from "../AnalyticsKit";
import { Donut, HBar, Trend, TreemapChart, PALETTE } from "../charts";

export default function CommercialLens({ filters, openDrill, onOpenClaim, reloadKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    apiFetch(`/analytics/commercial?${buildQS(filters)}`)
      .then((d) => { if (live) { setData(d); setLoading(false); } })
      .catch((e) => { if (live) { setError(e.message || String(e)); setLoading(false); } });
    return () => { live = false; };
  }, [filters, reloadKey]);

  const m = (v) => fmt.money(v);
  const insured = useMemo(() => (data?.by_insured || []).map((r) => ({ name: r.name, value: r.count, _cid: r.company_id })), [data]);
  const vessels = useMemo(() => (data?.by_vessel || []).map((r) => ({ name: r.name, value: r.count, _vid: r.vessel_id })), [data]);
  const lines = useMemo(() => (data?.by_business_line || []).map((r, i) => ({ name: r.name, value: r.count, color: PALETTE[i % PALETTE.length] })), [data]);
  const uw = useMemo(() => (data?.underwriters || []).map((r) => ({ name: r.name, value: r.count, _cid: r.company_id })), [data]);
  const trend = data?.new_claims_trend;
  const trendSeries = useMemo(() => (trend?.series || []).map((s, i) => ({ key: s.key, label: s.key, color: PALETTE[i % PALETTE.length] })), [trend]);

  if (error) return <div className="an-error">Couldn't load commercial analytics: {error}</div>;
  const L = loading && !data;
  const t = data?.totals || {};

  return (
    <div className="an-stack">
      <div className="an-kpis" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
        <KpiCard label="Active Insureds" tone="blue" value={L ? "…" : num(t.insureds)} sub="in current filter" />
        <KpiCard label="Vessels" value={L ? "…" : num(t.vessels)} sub="with claims" />
        <KpiCard label="Top Client" tone="navy" value={L ? "…" : (data?.high_exposure?.[0]?.name || "—")}
                 sub={data?.high_exposure?.[0] ? `${m(data.high_exposure[0].total_reserve)} reserve` : ""} />
      </div>

      <div className="an-grid">
        <ChartCard title="Claims by Insured" subtitle="top 15; click to drill" wide loading={L} empty={!insured.length}
                   height={Math.max(260, insured.length * 24)}>
          <HBar data={insured} height={Math.max(240, insured.length * 24)}
                onSegmentClick={(p) => p._cid && openDrill("insured", String(p._cid), `${p.name} claims`)} color="#3b6fd4" />
        </ChartCard>

        <ChartCard title="Claims by Business Line" loading={L} empty={!lines.length}>
          <Donut data={lines} onSegmentClick={(p) => openDrill("business_line", p.name, `${p.name} claims`)} />
        </ChartCard>

        <ChartCard title="Claims by Vessel" subtitle="top 15 vessels" loading={L} empty={!vessels.length}>
          <TreemapChart data={vessels} />
        </ChartCard>

        <ChartCard title="New Claims Trend — Top Clients" subtitle="monthly opened, rolling 12 months" wide
                   loading={L} empty={!trendSeries.length}>
          <Trend data={trend?.rows || []} xKey="month" series={trendSeries} area={false} />
        </ChartCard>

        <ChartCard title="High-Exposure Clients" subtitle="open reserve by insured" wide loading={L} empty={!data?.high_exposure?.length}>
          <table className="an-exposure-table">
            <thead><tr><th>Insured</th><th className="r">Open</th><th className="r">Reserve</th>
                       <th className="r">Paid</th><th className="r">Remaining</th><th></th></tr></thead>
            <tbody>
              {(data?.high_exposure || []).map((r) => (
                <tr key={r.company_id}>
                  <td>{r.name}</td>
                  <td className="r num">{num(r.open_count)}</td>
                  <td className="r num">{m(r.total_reserve)}</td>
                  <td className="r num">{m(r.total_paid)}</td>
                  <td className="r num">{m(r.remaining)}</td>
                  <td><button className="an-link" onClick={() => openDrill("insured", String(r.company_id), `${r.name} claims`)}>Claims ↗</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartCard>

        <ChartCard title="Underwriter / Market Exposure" subtitle="open claims per underwriter" wide
                   controls={<span className="pill pill-amber pill-sm" title="Reserve attribution is full-claim per associated underwriter, not slip-share weighted.">Not slip-weighted</span>}
                   loading={L} empty={!uw.length}
                   unavailable={!L && data && !uw.length ? { status: "Partial", reason: "No active underwriter parties on the filtered open claims." } : undefined}
                   height={Math.max(240, uw.length * 24)}>
          <HBar data={uw} height={Math.max(220, uw.length * 24)}
                onSegmentClick={(p) => p._cid && openDrill("underwriter", String(p._cid), `${p.name} — open claims`)} color="#14b8a6" />
        </ChartCard>
      </div>
    </div>
  );
}

function num(n) { return n == null ? "—" : Number(n).toLocaleString(); }
